<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { formatDate } from '@vueuse/core';

import Button from 'primevue/button';

import { PRODUCER_URL } from '@/config/appConfig';
import { pollTypes } from '@/config/interactiveTypes';

import useUserStore from '@/stores/user';

import useGraphicsService from '@/services/graphics';
import useLiveService from '@/services/live';
import usePanelsService from '@/services/panels';
import useBoardsService from '@/services/boards';
import usePagesService from '@/services/pages';
import useShowsService from '@/services/shows';
import * as Polls from '@/services/polls';
import useCreateNewDialog from '@/composables/useCreateNewDialog';
import { getSearchTypeMeta } from '@/utils/searchTypeMeta';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const TYPE_KEY_ALL = 'all';

const ALL_META = {
  label: 'All',
  badgeLabel: 'Item',
  color: 'var(--primary-400)',
  backgroundColor: 'var(--primary-100)',
  thumbnailBackgroundColor: 'var(--primary-100)',
  icon: 'far fa-layer-group',
};

const filterTypeMeta = computed(() => ({
  [TYPE_KEY_ALL]: ALL_META,
  shows: getSearchTypeMeta('shows'),
  graphics: getSearchTypeMeta('graphics'),
  polls: getSearchTypeMeta('polls'),
  classicPolls: getSearchTypeMeta('classicPolls'),
  smartDisplays: getSearchTypeMeta('smartDisplays'),
  classic: getSearchTypeMeta('classic'),
  boards: getSearchTypeMeta('boards'),
  socialDisplays: getSearchTypeMeta('socialDisplays'),
  pages: getSearchTypeMeta('pages'),
  producer: getSearchTypeMeta('producer'),
}));

const getCardItemMeta = (typeKey) => filterTypeMeta.value[typeKey] ?? ALL_META;

const filterOptions = computed(() => [
  {
    key: TYPE_KEY_ALL,
    dataEntity: 'app-jump-back-in-filter-all',
    dataTest: 'filter-all',
  },
  {
    key: 'shows',
    dataEntity: 'app-jump-back-in-filter-shows',
    dataTest: 'filter-shows',
  },
  {
    key: 'boards',
    dataEntity: 'app-jump-back-in-filter-social-boards',
    dataTest: 'filter-social-boards',
  },
  {
    key: 'graphics',
    tooltip: 'Previously called Graphics',
    dataEntity: 'app-jump-back-in-filter-graphics',
    dataTest: 'filter-graphics',
  },
  {
    key: 'polls',
    dataEntity: 'app-jump-back-in-filter-polls',
    dataTest: 'filter-polls',
  },
  {
    key: 'socialDisplays',
    dataEntity: 'app-jump-back-in-filter-social-displays',
    dataTest: 'filter-social-displays',
  },
  {
    key: 'classic',
    dataEntity: 'app-jump-back-in-filter-classic',
    dataTest: 'filter-classic',
  },
  {
    key: 'pages',
    dataEntity: 'app-jump-back-in-filter-pages',
    dataTest: 'filter-pages',
  },
  {
    key: 'producer',
    dataEntity: 'app-jump-back-in-filter-producer',
    dataTest: 'filter-producer',
  },
].map((filter) => ({
  ...filter,
  label: getCardItemMeta(filter.key).label,
  color: getCardItemMeta(filter.key).color,
})));

const MAX_ITEMS = 7;

const createNewDialog = useCreateNewDialog();
const { currentTeamId } = storeToRefs(useUserStore());
const activeFilter = ref(TYPE_KEY_ALL);

const graphicsQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'modified_time',
  sortDir: 'desc',
});

const {
  data: graphicsData,
  isFetching: isFetchingRecentGraphics,
  execute: getTemplates,
} = useGraphicsService().getTemplates(graphicsQuery, { immediate: false, refetch: true });
const recentGraphics = computed(() => graphicsData.value?.templates ?? graphicsData.value);

const socialDisplayQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'modified_time',
  sortDir: 'desc',
});

const {
  data: socialDisplayData,
  isFetching: isFetchingRecentSocialDisplays,
  execute: fetchSocialDisplays,
} = useLiveService().getThemes(socialDisplayQuery, { immediate: false, refetch: true });
const socialDisplays = computed(() => socialDisplayData.value?.themes ?? []);

const pagesQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'updatedAt',
  sortDir: 'desc',
});

const {
  data: pagesData,
  isFetching: isFetchingRecentPages,
  execute: fetchPages,
} = usePagesService().getPages(pagesQuery, { immediate: false, refetch: true });
const recentPages = computed(() => pagesData.value?.pages ?? []);

const showsQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'updatedAt',
  sortDir: 'desc',
});

const {
  data: showsData,
  isFetching: isFetchingRecentShows,
  execute: fetchShows,
} = useShowsService().getShows(showsQuery, { immediate: false, refetch: true });
const recentShows = computed(() => showsData.value?.shows ?? []);

const interactiveDisplaysQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'modified_time',
  sortDir: 'desc',
  types: 'poll-results',
});

const {
  data: interactiveDisplaysData,
  isFetching: isFetchingInteractiveDisplays,
  execute: fetchInteractiveDisplays,
} = usePanelsService().getPanels(interactiveDisplaysQuery, { immediate: false, refetch: true });
const recentInteractiveDisplays = computed(() => interactiveDisplaysData.value?.panels ?? []);

const pollsQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'modified_time',
  sortDir: 'desc',
  types: pollTypes,
});

const {
  data: pollsData,
  isFetching: isFetchingPolls,
  execute: fetchPolls,
} = usePanelsService().getPanels(pollsQuery, { immediate: false, refetch: true });
const recentClassicPolls = computed(() => pollsData.value?.panels ?? []);

const newPollsQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'updatedAt',
  sortDir: 'desc',
});

const {
  data: newPollsData,
  isFetching: isFetchingNewPolls,
  execute: fetchNewPolls,
} = Polls.getPolls(newPollsQuery, { immediate: false, refetch: true });

const recentNewPolls = computed(() => (newPollsData.value?.polls ?? [])
  .map((poll) => ({
    ...poll,
    _id: poll.pollId,
    modified_time: poll.updatedAt,
  }))
  .slice(0, MAX_ITEMS));

const boardsQuery = ref({
  page: 1,
  limit: MAX_ITEMS,
  sortBy: 'modified_time',
  sortDir: 'desc',
});

const {
  data: boardsData,
  isFetching: isFetchingRecentBoards,
} = useBoardsService().getBoards(currentTeamId, boardsQuery, { refetch: true });
const recentBoards = computed(() => boardsData.value?.boards ?? []);

const productionsQuery = ref({
  pageNumber: 1,
  include_teams: false,
});

const {
  data: productionsData,
  isFetching: isFetchingRecentProductions,
} = useLiveService().getProductions(productionsQuery, { refetch: true });

const recentProductions = computed(() => (productionsData.value?.productions ?? []).slice(0, MAX_ITEMS));

const makeFormattedDate = (date) => {
  if (!date) return undefined;
  const now = new Date();
  const d = new Date(date);
  const diffDays = Math.floor(
    (now.setHours(0, 0, 0, 0) - d.setHours(0, 0, 0, 0)) / 86400000,
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 3) return `${diffDays} days ago`;
  return formatDate(new Date(date), 'MMM D, YYYY');
};

const toTimestamp = (date) => {
  const parsed = new Date(date).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildCardItem = (typeKey, itemData) => {
  const meta = getCardItemMeta(typeKey);
  const tagBackgroundColor = meta.backgroundColor;
  const thumbnailBackgroundColor = meta.thumbnailBackgroundColor ?? meta.backgroundColor;

  return {
    ...meta,
    typeKey,
    typeLabel: meta.badgeLabel,
    icon: meta.icon,
    typeTagStyle: {
      color: meta.color,
      backgroundColor: tagBackgroundColor,
    },
    thumbnailFallbackStyle: {
      color: meta.color,
      backgroundColor: thumbnailBackgroundColor,
    },
    ...itemData,
  };
};

const normalizeGraphics = computed(() => (
  (recentGraphics.value ?? []).map((item) => buildCardItem('graphics', {
    id: item._id,
    title: item.title,
    modifiedAt: item.modified_time,
    modifiedTimestamp: toTimestamp(item.modified_time),
    thumbnail: item.preview_url,
    to: { name: 'graphic-editor', params: { graphicId: item._id } },
    dataEntity: `app-jump-back-in-graphic-${item._id}`,
    dataTest: `graphic-${item._id}`,
  }))
));

const normalizePolls = computed(() => (
  (recentNewPolls.value ?? []).map((item) => buildCardItem('polls', {
    id: item.pollId,
    title: item.title,
    modifiedAt: item.modified_time,
    modifiedTimestamp: toTimestamp(item.modified_time),
    thumbnail: item?.displays?.[0]?.previewUrl,
    to: { name: 'poll-editor', params: { pollId: item.pollId } },
    dataEntity: `app-jump-back-in-poll-${item.pollId}`,
    dataTest: `poll-${item.pollId}`,
  }))
));

const normalizeClassicPolls = computed(() => (
  (recentClassicPolls.value ?? []).map((item) => buildCardItem('classicPolls', {
    id: item._id,
    title: item.title,
    modifiedAt: item.modified_time,
    modifiedTimestamp: toTimestamp(item.modified_time),
    thumbnail: item.preview_url,
    to: { name: 'panel-editor', params: { panelId: item._id } },
    dataEntity: `app-jump-back-in-classic-poll-${item._id}`,
    dataTest: `classic-poll-${item._id}`,
  }))
));

const normalizeClassicDisplays = computed(() => (
  (recentInteractiveDisplays.value ?? []).map((item) => buildCardItem('smartDisplays', {
    id: item._id,
    title: item.title,
    modifiedAt: item.modified_time,
    modifiedTimestamp: toTimestamp(item.modified_time),
    thumbnail: item.preview_url,
    to: { name: 'panel-editor', params: { panelId: item._id } },
    dataEntity: `app-jump-back-in-classic-display-${item._id}`,
    dataTest: `classic-display-${item._id}`,
  }))
));

const normalizeSocialDisplays = computed(() => (
  (socialDisplays.value ?? []).map((item) => buildCardItem('socialDisplays', {
    id: item._id,
    title: item.title,
    modifiedAt: item.modified_time,
    modifiedTimestamp: toTimestamp(item.modified_time),
    thumbnail: item.preview_url,
    to: { name: 'display-editor', params: { displayId: item._id } },
    dataEntity: `app-jump-back-in-social-display-${item._id}`,
    dataTest: `social-display-${item._id}`,
  }))
));

const normalizePages = computed(() => (
  (recentPages.value ?? []).map((item) => buildCardItem('pages', {
    id: item.pageId,
    title: item.title,
    modifiedAt: item.updatedAt || item.createdAt,
    modifiedTimestamp: toTimestamp(item.updatedAt || item.createdAt),
    thumbnail: item.preview_url,
    to: { name: 'page-editor', params: { pageId: item.pageId } },
    dataEntity: `app-jump-back-in-page-${item.pageId}`,
    dataTest: `page-${item.pageId}`,
  }))
));

const normalizeShows = computed(() => (
  (recentShows.value ?? []).map((item) => buildCardItem('shows', {
    id: item.id,
    title: item.title,
    modifiedAt: item.updatedAt || item.createdAt,
    modifiedTimestamp: toTimestamp(item.updatedAt || item.createdAt),
    thumbnail: item.preview_url,
    to: { name: 'show-editor', params: { showId: item.id } },
    dataEntity: `app-jump-back-in-show-${item.id}`,
    dataTest: `show-${item.id}`,
  }))
));

const normalizeProducer = computed(() => (
  (recentProductions.value ?? []).map((item) => buildCardItem('producer', {
    id: item._id,
    title: item.title,
    modifiedAt: item.modified,
    modifiedTimestamp: toTimestamp(item.modified),
    thumbnail: item.preview_url,
    href: `${PRODUCER_URL}/#/${item._id}`,
    external: true,
    dataEntity: `app-jump-back-in-producer-${item._id}`,
    dataTest: `production-${item._id}`,
  }))
));

const normalizeBoards = computed(() => (
  (recentBoards.value ?? []).map((item) => buildCardItem('boards', {
    id: item._id,
    title: item.name,
    modifiedAt: item.modified_time,
    modifiedTimestamp: toTimestamp(item.modified_time),
    thumbnail: item.preview_url,
    to: { name: 'board-editor', params: { boardId: item._id, tab: 'featured' } },
    dataEntity: `app-jump-back-in-social-board-${item._id}`,
    dataTest: `board-${item._id}`,
  }))
));

const normalizeClassic = computed(() => (
  [...normalizeClassicPolls.value, ...normalizeClassicDisplays.value]
    .sort((a, b) => b.modifiedTimestamp - a.modifiedTimestamp)
    .slice(0, MAX_ITEMS)
));

const itemsByFilter = computed(() => ({
  graphics: normalizeGraphics.value,
  polls: normalizePolls.value,
  classic: normalizeClassic.value,
  boards: normalizeBoards.value,
  socialDisplays: normalizeSocialDisplays.value,
  pages: normalizePages.value,
  shows: normalizeShows.value,
  producer: normalizeProducer.value,
}));

const allItems = computed(() => Object.values(itemsByFilter.value)
  .flat()
  .sort((a, b) => b.modifiedTimestamp - a.modifiedTimestamp)
  .slice(0, MAX_ITEMS));

const displayedItems = computed(() => (
  activeFilter.value === TYPE_KEY_ALL
    ? allItems.value
    : itemsByFilter.value[activeFilter.value] ?? []
));

const isLoadingByFilter = computed(() => ({
  graphics: isFetchingRecentGraphics.value,
  polls: isFetchingNewPolls.value,
  classic: isFetchingPolls.value || isFetchingInteractiveDisplays.value,
  boards: isFetchingRecentBoards.value,
  socialDisplays: isFetchingRecentSocialDisplays.value,
  pages: isFetchingRecentPages.value,
  shows: isFetchingRecentShows.value,
  producer: isFetchingRecentProductions.value,
}));

const isLoading = computed(() => (
  activeFilter.value === TYPE_KEY_ALL
    ? Object.values(isLoadingByFilter.value).some(Boolean)
    : isLoadingByFilter.value[activeFilter.value]
));

const activeFilterMeta = computed(() => getCardItemMeta(activeFilter.value));

const selectFilter = (filterKey) => {
  activeFilter.value = filterKey;
};

const getItemLinkBindings = (item) => (
  item.external
    ? {
      href: item.href,
      target: '_blank',
      rel: 'noopener noreferrer',
    }
    : {
      to: item.to,
    }
);

const openCreateForFilter = () => {
  switch (activeFilter.value) {
    case 'graphics':
      createNewDialog.open('graphic');
      break;
    case 'polls':
      createNewDialog.open('poll');
      break;
    case 'socialDisplays':
      createNewDialog.open('socialDisplay');
      break;
    case 'pages':
      createNewDialog.open('page');
      break;
    case 'shows':
      createNewDialog.open('show');
      break;
    case 'boards':
      createNewDialog.open('socialBoard');
      break;
    case 'producer':
      window.location.assign(`${PRODUCER_URL}/${currentTeamId.value}`);
      break;
    default:
      createNewDialog.open();
      break;
  }
};

onMounted(() => {
  getTemplates();
  fetchNewPolls();
  fetchPolls();
  fetchInteractiveDisplays();
  fetchSocialDisplays();
  fetchPages();
  fetchShows();
});
</script>

<template>
  <section
    class="flex flex-column gap-4 m-top-6 m-left-4 m-right-4 md:px-4 mt-6"
    data-test="jump-back-in"
    data-entity="app-jump-back-in">
    <div class="flex align-items-center justify-content-between gap-4 flex-wrap">
      <h2 class="jump-back-in__title flex align-items-center gap-2 m-0 font-bold">
        <FontAwesomeIcon icon="far fa-clock" />
        Jump Back In
      </h2>
      <div
        class="flex flex-wrap gap-2 justify-content-end"
        role="tablist"
        aria-label="Jump Back In filters">
        <Button
          v-for="filter in filterOptions"
          :key="filter.key"
          outlined
          severity="secondary"
          class="jump-back-in__filter"
          :class="{ 'jump-back-in__filter--active': activeFilter === filter.key }"
          :data-test="filter.dataTest"
          :data-entity="filter.dataEntity"
          :aria-selected="activeFilter === filter.key"
          @click="selectFilter(filter.key)"
        >
          <span class="p-button-label">{{ filter.label }}</span>
          <span
            v-if="filter.tooltip"
            v-tooltip.top="filter.tooltip"
            class="jump-back-in__filter-info"
            @click.stop
          >
            <FontAwesomeIcon icon="far fa-circle-info" />
          </span>
        </Button>
      </div>
    </div>

    <div class="jump-back-in__cards gap-3" data-entity="app-jump-back-in-cards">
      <template v-if="isLoading">
        <div
          v-for="index in MAX_ITEMS"
          :key="`skeleton-${index}`"
          class="asset-card asset-card--skeleton" />
      </template>

      <template v-else>
        <component
          :is="item.external ? 'a' : 'router-link'"
          v-for="item in displayedItems"
          :key="`${item.typeKey}-${item.id}`"
          class="asset-card flex align-items-stretch cursor-pointer"
          :class="{ 'asset-card--producer': item.typeKey === 'producer' }"
          v-bind="getItemLinkBindings(item)"
          :style="{ '--jump-card-color': item.color }"
          :data-test="item.dataTest"
          :data-entity="item.dataEntity"
        >
          <span class="asset-card__type-tag" :style="item.typeTagStyle">
            {{ item.typeLabel }}
          </span>
          <div
            v-if="item.thumbnail"
            :class="[
              'asset-card__thumbnail',
              'flex',
              'align-items-center',
              'justify-content-center',
              'flex-shrink-0',
              { 'asset-card__thumbnail--producer': item.typeKey === 'producer' },
            ]"
          >
            <img
              :src="item.thumbnail"
              :alt="item.title"
              class="asset-card__thumbnail-image">
          </div>
          <div class="asset-card__content flex flex-column justify-content-center flex-1 p-4">
            <div class="asset-card__title">{{ item.title }}</div>
            <div class="asset-card__meta">
              <span class="asset-card__meta-label">Edited:</span>
              {{ makeFormattedDate(item.modifiedAt) }}
            </div>
          </div>
        </component>

        <button
          type="button"
          class="asset-card asset-card--create flex align-items-stretch cursor-pointer"
          :style="{ '--jump-card-color': activeFilterMeta.color }"
          data-entity="app-jump-back-in-create"
          @click="openCreateForFilter">
          <div class="flex flex-column justify-content-center flex-1 p-4">
            <div class="asset-card__title"><FontAwesomeIcon icon="far fa-plus" /> Create New</div>
          </div>
        </button>

        <div
          v-if="!displayedItems.length"
          class="text-color-secondary text-center col-12">No recent files</div>
      </template>
    </div>
  </section>
</template>

<style lang="scss">
.jump-back-in__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
}

.jump-back-in__title {
  font-size: 1.25em;
  font-weight: 700;
}

.jump-back-in__filter.p-button {
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 999px;
  box-shadow: none;
  color: var(--text-color-secondary);
  padding: 0.35rem 0.9rem;
  position: relative;
  overflow: visible;
}

.jump-back-in__filter--active.p-button {
  border-color: var(--primary-400);
  color: var(--primary-500);
}

.jump-back-in__filter-info {
  position: absolute;
  top: -0.35rem;
  right: -0.35rem;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  background: var(--surface-card);
  border-radius: 50%;
  cursor: help;
}

.jump-back-in__filter-info:hover {
  color: var(--primary-500);
}

.asset-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-300);
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(17, 24, 39, 0.08);
  color: inherit;
  overflow: hidden;
  position: relative;
  text-decoration: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:link,
  &:visited,
  &.router-link-active,
  &.router-link-exact-active {
    color: inherit;
  }

  &:hover {
    box-shadow: 0 6px 20px rgba(17, 24, 39, 0.14);
    transform: translateY(-1px);
  }

  &__thumbnail {
    aspect-ratio: 16 / 9;
    background: var(--surface-100);
    color: var(--jump-card-color, var(--secondary-500));
    flex: 0 0 auto;
    overflow: hidden;
    width: 120px;
  }

  &__thumbnail-image {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  &__thumbnail-icon {
    font-size: 1.1rem;
    opacity: 0.9;
  }

  &__content {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__title {
    color: inherit;
    display: block;
    font-size: 0.95rem;
    font-weight: 400;
    line-height: 1.3;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
    margin-top: 0.1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta-label {
    font-size: 0.85em;
    opacity: 0.7;
  }

  &__type-tag {
    border: none;
    border-radius: 4px;
    display: inline-block;
    font-size: 0.5rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: 1;
    padding: 0.18rem 0.4rem;
    position: absolute;
    right: 0.5rem;
    text-transform: uppercase;
    top: 0.5rem;
    z-index: 1;
  }

  &--create {
    border: 1px dashed rgba(0, 0, 0, 0.35);
    cursor: pointer;

    &:hover {
      border-color: var(--jump-card-color, var(--secondary-400));
      color: var(--jump-card-color, var(--secondary-500));
    }
  }

  &__thumbnail--create {
    background: transparent;
    color: currentColor;
  }

  &--producer {
    background: #212529;
    border-color: #2f363c;
    color: #fff;

    .asset-card__title {
      color: #fff;
    }

    .asset-card__meta {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  &__thumbnail--producer {
    background: linear-gradient(180deg, #0f172a 0%, #111827 100%);
    color: #00c4dd;
  }

  &--skeleton {
    background: linear-gradient(100deg, #f3f4f6 35%, #e5e7eb 50%, #f3f4f6 65%);
    background-size: 200% 100%;
    min-height: 66px;
    pointer-events: none;
    animation: jump-back-in-skeleton 1.2s ease-in-out infinite;
  }
}

@keyframes jump-back-in-skeleton {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 1024px) {
  .jump-back-in {
    &__header {
      align-items: flex-start;
      flex-direction: column;
    }

    &__filters {
      justify-content: flex-start;
    }
  }
}

.dark-theme {
  .jump-back-in__filter.p-button {
    border-color: rgba(255, 255, 255, 0.22);
    color: rgba(255, 255, 255, 0.85);
  }

  .jump-back-in__filter--active.p-button {
    border-color: var(--primary-300);
    box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.25);
    color: var(--primary-200);
  }

  .asset-card {
    background: var(--surface-b);
    border-color: rgba(255, 255, 255, 0.16);
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.35);

    &__thumbnail {
      background: rgba(255, 255, 255, 0.04);
    }

    &__meta {
      color: rgba(255, 255, 255, 0.68);
    }

    &--create {
      border-color: rgba(255, 255, 255, 0.3);
    }

    &--producer {
      background: #101418;
      border-color: #00c4dd;
      box-shadow: 0 2px 20px rgba(6, 182, 212, 0.35);
      color: #ffffff;

      .asset-card__meta {
        color: rgba(255, 255, 255, 0.82);
      }
    }

    &__thumbnail--producer {
      background: linear-gradient(180deg, #09111f 0%, #0b1320 100%);
      color: #00c4dd;
    }

    &--skeleton {
      background: linear-gradient(100deg, #1f2937 35%, #374151 50%, #1f2937 65%);
      background-size: 200% 100%;
    }
  }
}
</style>
